
import { useLanguage } from "@/contexts/LanguageContext";
import { useSimpleStepWizard } from "./hooks/useSimpleStepWizard";
import { useUnsavedChanges } from "./hooks/useUnsavedChanges";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";
import { AutoSaveIndicator } from "./AutoSaveIndicator";
import { RetryAutoSaveButton } from "./RetryAutoSaveButton";
import InformationStep from "./steps/InformationStep";
import LocationsStep from "./steps/LocationsStep";
import TimeStep from "./steps/TimeStep";
import ContentStep from "./steps/ContentStep";
import PaymentStep from "./steps/PaymentStep";
import { StepIndicator, defaultSteps } from "./components/StepIndicator";
import AIAssistantButton from "./components/AIAssistantButton";
import { useParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { locations } from "@/data/locations";
import { resolveSuggestedLocations } from "@/utils/locationMatching";

interface StepWizardProps {
  onSubmit: () => void;
  isSubmitting: boolean;
  campaignId?: string;
  mode?: 'create' | 'draft' | 'edit';
}

export const StepWizard = ({ onSubmit, isSubmitting, campaignId, mode = 'create' }: StepWizardProps) => {
  const { language } = useLanguage();
  const { draftId } = useParams();
  
  const {
    currentStep,
    campaignName,
    formData,
    selectedLocations,
    duration,
    devices,
    useLocationSpecificCreatives,
    useMediaOwnerSpecificCreatives,
    isLoading,
    autoSaveStatus,
    lastSaveTime,
    validationErrors,
    // Creative upload functionality (now integrated)
    creatives,
    locationCreatives,
    mediaOwnerCreatives,
    isCreativeUploading: isUploading,
    uploadProgress,
    fileInputRef,
    handleFileChange,
    removeCreative,
    handleAreaClick,
    handleLocationFileChange,
    handleMediaOwnerFileChange,
    removeLocationCreative,
    removeMediaOwnerCreative,
    handleUrlSubmit,
    handleLocationUrlSubmit,
    handleMediaOwnerUrlSubmit,
    updateLocationCreativeDays,
    updateMediaOwnerCreativeDays,
    // Navigation and handlers
    handleNext,
    handleBack,
    handleStepClick,
    handleInputChange,
    setCampaignName,
    setSelectedLocations,
    setDuration,
    setUseLocationSpecificCreatives,
    setUseMediaOwnerSpecificCreatives
  } = useSimpleStepWizard(onSubmit, draftId, campaignId, mode);

  // Track unsaved changes for edit mode
  const {
    hasUnsavedChanges,
    showDialog,
    handleConfirmLeave,
    handleCancelLeave,
    markAsSaved
  } = useUnsavedChanges({
    mode: mode || 'create',
    campaignName,
    formData,
    selectedLocations,
    duration,
    isLoading,
    autoSaveStatus
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="mt-4 text-muted-foreground">Loading campaign...</p>
        </div>
      </div>
    );
  }

  // Add device setter that syncs with locations
  const setDevices = (newDevices: number) => {
    // This is handled automatically by setSelectedLocations
    console.log('Devices set to:', newDevices);
  };

  // Handle AI campaign suggestions
  const handleCampaignSuggestion = (suggestion: any) => {
    console.log('StepWizard: Received AI campaign suggestion:', suggestion);
    
    try {
      // Apply campaign name
      if (suggestion.campaignName && suggestion.campaignName !== campaignName) {
        console.log('StepWizard: Setting campaign name', suggestion.campaignName);
        setCampaignName(suggestion.campaignName);
      }

      // Apply duration
      if (suggestion.duration && suggestion.duration !== duration) {
        // Compute and save calendar dates first so the calendar picks them up on remount
        const minDate = new Date();
        minDate.setHours(minDate.getHours() + 48);
        
        const startDate = new Date(minDate);
        startDate.setHours(12, 0, 0, 0); // Set to noon
        
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + suggestion.duration - 1);
        endDate.setHours(23, 59, 59, 999); // Set to end of day
        
        // Save dates to localStorage so calendar component can display them
        localStorage.setItem('startDate', startDate.toISOString());
        localStorage.setItem('endDate', endDate.toISOString());
        // Also store exact YYYY-MM-DD for compatibility with other modules
        const yyyyMmDd = (d: Date) => d.toISOString().split('T')[0];
        localStorage.setItem('startDate_exact', yyyyMmDd(startDate));
        localStorage.setItem('endDate_exact', yyyyMmDd(endDate));
        
        console.log('StepWizard: Set calendar dates:', {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          duration: suggestion.duration
        });
        
        console.log('StepWizard: Setting duration', suggestion.duration);
        setDuration(suggestion.duration);
      }

      // Apply company information if provided
      if (suggestion.companyName || suggestion.brandName || suggestion.personInCharge) {
        console.log('StepWizard: Applying form data updates');
        
        // Use handleInputChange to update individual form fields
        const fieldsToUpdate = {
          companyName: suggestion.companyName || formData.companyName || '',
          brandName: suggestion.brandName || formData.brandName || '',
          personInCharge: suggestion.personInCharge || formData.personInCharge || '',
          jobTitle: suggestion.jobTitle || formData.jobTitle || '',
          email: suggestion.email || formData.email || '',
          mobile: suggestion.mobile || formData.mobile || ''
        };
        
        // Simulate form input changes to trigger the form update
        Object.entries(fieldsToUpdate).forEach(([key, value]) => {
          if (value && value !== formData[key as keyof typeof formData]) {
            const event = {
              target: { name: key, value: value }
            } as React.ChangeEvent<HTMLInputElement>;
            handleInputChange(event);
          }
        });
      }

      // Apply selected locations
      if (suggestion.suggestedLocations && suggestion.suggestedLocations.length > 0) {
        console.log('🔥 STEPWIZARD: Applying suggested locations');
        console.log('🔥 STEPWIZARD: Raw suggestion locations:', suggestion.suggestedLocations);
        
        const normalize = (s: string) => (s || '').toLowerCase().trim();
        const normText = (s: any) =>
          (s ?? '')
            .toString()
            .toLowerCase()
            .normalize('NFKD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^\p{L}\p{N}\s]/gu, '')
            .replace(/\s+/g, ' ')
            .trim();
        const normRegion = (r?: string) => {
          const key = normText(r);
          const map: Record<string, string> = {
            'al madinah': 'medina',
            'madinah': 'medina',
            'medinah': 'medina',
            'al madinah al munawwarah': 'medina',
            'ar riyadh': 'riyadh'
          };
          return map[key] || key;
        };
        const normOwner = (o?: string) => {
          const key = normText(o);
          const map: Record<string, string> = {
            'tableads': 'tableads',
            'alan': 'alan',
            'waki': 'waki',
            'attention': 'attention'
          };
          return map[key] || key;
        };

        const selectedLocationObjects = resolveSuggestedLocations(suggestion.suggestedLocations, locations as any);
        console.log(`🔥 STEPWIZARD: Final selected locations (${selectedLocationObjects.length}):`, selectedLocationObjects);
        
        if (selectedLocationObjects.length > 0) {
          setSelectedLocations(selectedLocationObjects as any);
        }
      }

      // Store additional budget and strategy information
      if (suggestion.recommendedBudget) {
        const budgetCalculations = {
          totalBudget: suggestion.recommendedBudget,
          dailyCost: Math.round(suggestion.recommendedBudget / (suggestion.duration || 14)),
          duration: suggestion.duration,
          campaignStrategy: suggestion.campaignStrategy,
          targetAudience: suggestion.targetAudience
        };
        localStorage.setItem('budgetCalculations', JSON.stringify(budgetCalculations));
      }


      console.log('StepWizard: Successfully applied AI campaign suggestion');
      
    } catch (error) {
      console.error('StepWizard: Error applying AI suggestion:', error);
    }
  };

  // Create AI Assistant Button
  const aiAssistantButton = (
    <AIAssistantButton
      currentStep={currentStep}
      formData={formData}
      selectedLocations={selectedLocations}
      budget={duration * devices * 10}
      onCampaignSuggestion={handleCampaignSuggestion}
    />
  );

  return (
    <div className={`w-full ${language === 'ar' ? 'text-right' : 'text-left'} relative`}>
      <div className="mb-6 space-y-4">
        <StepIndicator 
          steps={defaultSteps}
          currentStep={currentStep} 
          onStepClick={handleStepClick} 
        />
        
        {/* Auto-save indicator for edit mode */}
        {mode === 'edit' && (
          <div className="flex items-center justify-end gap-2">
            <AutoSaveIndicator 
              status={autoSaveStatus} 
              lastSaveTime={lastSaveTime || undefined}
              validationErrors={validationErrors}
            />
            <RetryAutoSaveButton onRetry={() => handleNext()} />
          </div>
        )}
      </div>
      
      <div className="mt-8">
        {currentStep === 0 && (
          <LocationsStep
            selectedLocations={selectedLocations}
            onDeviceSelect={setDevices}
            onLocationsChange={setSelectedLocations}
            onNext={handleNext}
            onBack={handleBack}
            aiAssistantButton={aiAssistantButton}
          />
        )}
        
        {currentStep === 1 && (
          <TimeStep
            value={duration}
            onChange={(values) => setDuration(values[0])}
            onNext={handleNext}
            onBack={handleBack}
            selectedLocations={selectedLocations}
            aiAssistantButton={aiAssistantButton}
          />
        )}
        
        {currentStep === 2 && (
          <ContentStep
            creatives={creatives}
            locationCreatives={locationCreatives}
            mediaOwnerCreatives={mediaOwnerCreatives}
            selectedLocations={selectedLocations}
            onFileChange={handleFileChange}
            onRemoveCreative={removeCreative}
            onLocationFileChange={handleLocationFileChange}
            onRemoveLocationCreative={removeLocationCreative}
            onUpdateLocationCreativeDays={updateLocationCreativeDays}
            onMediaOwnerFileChange={handleMediaOwnerFileChange}
            onRemoveMediaOwnerCreative={removeMediaOwnerCreative}
            onUpdateMediaOwnerCreativeDays={updateMediaOwnerCreativeDays}
            fileInputRef={fileInputRef}
            onAreaClick={handleAreaClick}
            isUploading={isUploading}
            uploadProgress={uploadProgress}
            onNext={handleNext}
            onBack={handleBack}
            useLocationSpecificCreatives={useLocationSpecificCreatives}
            onToggleLocationSpecificCreatives={setUseLocationSpecificCreatives}
            useMediaOwnerSpecificCreatives={useMediaOwnerSpecificCreatives}
            onToggleMediaOwnerSpecificCreatives={setUseMediaOwnerSpecificCreatives}
            campaignName={campaignName}
            setCampaignName={setCampaignName}
            aiAssistantButton={aiAssistantButton}
            onUrlSubmit={handleUrlSubmit}
            onLocationUrlSubmit={handleLocationUrlSubmit}
            onMediaOwnerUrlSubmit={handleMediaOwnerUrlSubmit}
          />
        )}
        
        {currentStep === 3 && (
          <InformationStep 
            formData={formData}
            onChange={handleInputChange}
            onNext={handleNext}
            onBack={handleBack}
            aiAssistantButton={aiAssistantButton}
          />
        )}
        
        {currentStep === 4 && (
          <PaymentStep
            devices={devices}
            duration={duration}
            onSubmit={onSubmit}
            onBack={handleBack}
            isSubmitting={isSubmitting}
            selectedLocations={selectedLocations}
            onResetToFirstStep={() => handleStepClick(0)}
            aiAssistantButton={aiAssistantButton}
          />
        )}
      </div>

      {/* Unsaved Changes Warning Dialog */}
      <UnsavedChangesDialog
        isOpen={showDialog}
        onConfirm={handleConfirmLeave}
        onCancel={handleCancelLeave}
      />
    </div>
  );
};
